import React, { useState, useEffect, useMemo, useRef } from 'react';
import { userKey } from '../utils/storageHelper.js';
import { supabase } from '../lib/supabaseClient.js';
import checklistData from '../data/ComplianceChecklists.json';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { EUFlagIcon } from './EUFlagIcon.jsx';

// ── Storage helpers (lazy — evaluated at call time, NOT module-load time, so userKey() resolves to the logged-in user) ──
const getStorageKey = () => userKey('qais_checklist_progress');
const getSetupKey   = () => userKey('qais_checklist_setup');
const getEvidenceNotesKey = () => userKey('qais_checklist_evidence_notes');

async function saveProgress(checked) {
  // localStorage first — instant
  try { localStorage.setItem(getStorageKey(), JSON.stringify(checked)); } catch {}
  // Supabase in background — use cached session, not a fresh getUser() network call
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      await supabase.from('user_profiles').upsert(
        { user_id: session.user.id, checklist_progress: checked, updated_at: new Date().toISOString() },
        { onConflict: 'user_id' }
      );
    }
  } catch {}
}

async function loadProgress() {
  // Always load localStorage first — instant, no network needed
  let localData = {};
  try {
    const local = localStorage.getItem(getStorageKey());
    if (local) localData = JSON.parse(local);
  } catch {}

  // Then try Supabase in background — don't block UI on this
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      const { data } = await supabase.from('user_profiles')
        .select('checklist_progress').eq('user_id', session.user.id).single();
      if (data?.checklist_progress) {
        localStorage.setItem(getStorageKey(), JSON.stringify(data.checklist_progress));
        return data.checklist_progress;
      }
    }
  } catch {}

  return localData;
}

// Task #13 follow-up: per-evidence-line self-recorded notes (e.g. "see
// SharePoint/Policies/DataGovernance.pdf"). Stored as { [itemId]: [note0, note1, ...] }
// aligned by index with that item's evidence_needed array. Same table/row as
// checklist_progress, same RLS (auth.uid() = user_id) — no new policy needed.
async function saveEvidenceNotes(notes) {
  try { localStorage.setItem(getEvidenceNotesKey(), JSON.stringify(notes)); } catch {}
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      const { error } = await supabase.from('user_profiles').upsert(
        { user_id: session.user.id, checklist_evidence_notes: notes, updated_at: new Date().toISOString() },
        { onConflict: 'user_id' }
      );
      if (error) console.error('[checklist] saveEvidenceNotes failed:', error.message);
    }
  } catch (err) {
    console.error('[checklist] saveEvidenceNotes failed:', err.message);
  }
}

async function loadEvidenceNotes() {
  let localData = {};
  try {
    const local = localStorage.getItem(getEvidenceNotesKey());
    if (local) localData = JSON.parse(local);
  } catch {}

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      const { data, error } = await supabase.from('user_profiles')
        .select('checklist_evidence_notes').eq('user_id', session.user.id).single();
      if (error) console.error('[checklist] loadEvidenceNotes failed:', error.message);
      if (data?.checklist_evidence_notes) {
        localStorage.setItem(getEvidenceNotesKey(), JSON.stringify(data.checklist_evidence_notes));
        return data.checklist_evidence_notes;
      }
    }
  } catch (err) {
    console.error('[checklist] loadEvidenceNotes failed:', err.message);
  }

  return localData;
}

// ── Priority config ──
const PRIORITY = {
  critical: { label: 'Critical', color: '#ef4444', bg: '#fef2f2', border: '#fecaca', order: 0 },
  high:     { label: 'High',     color: '#f59e0b', bg: '#fffbeb', border: '#fde68a', order: 1 },
  medium:   { label: 'Medium',   color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe', order: 2 },
};

// ── Framework config ──
const FRAMEWORKS = {
  iso: {
    key: 'iso', label: 'ISO/IEC 42001', shortLabel: 'ISO 42001',
    icon: <span style={{ fontSize: '24px' }}>📘</span>, pdfIcon: '📘', color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe',
    refField: 'clause', refLabel: 'Clause', dataKey: 'iso42001_checklist',
    description: 'International AI Management System Standard — globally recognized certification',
  },
eu: {
    key: 'eu', label: 'EU AI Act', shortLabel: 'EU AI Act',
   icon: <EUFlagIcon size={36} blue="#4285F4" glow={false} />, pdfIcon: '🇪🇺', color: '#8b5cf6', bg: '#faf5ff', border: '#ddd6fe',
    refField: 'article', refLabel: 'Article', dataKey: 'eu_ai_act_high_risk_checklist',
    description: 'Legally binding EU regulation — mandatory for organizations operating in or selling to Europe',
  },
nist: {
    key: 'nist', label: 'NIST AI RMF', shortLabel: 'NIST RMF',
    icon: <span style={{ fontSize: '24px' }}>🏛️</span>, pdfIcon: '🏛️', color: '#06b6d4', bg: '#ecfeff', border: '#a5f3fc',
    refField: 'function', refLabel: 'Function', dataKey: 'nist_ai_rmf_checklist',
    description: 'Leading US AI Risk Management Framework — widely adopted by US agencies and enterprises',
  },

  // ── ADD FUTURE FRAMEWORKS HERE ──
  // To add a new framework:
  // 1. Add a new entry below with a unique key (e.g. 'soc2', 'gdpr', 'iso27001')
  // 2. Add the corresponding checklist JSON file to src/data/
  // 3. Import it in checklistData at the top of this file
  // 4. The framework will automatically appear in Setup Step 1 and all filters
  //
  // Example template:
  // future_framework: {
  //   key: 'future_framework',
  //   label: 'Framework Full Name',
  //   shortLabel: 'Short Name',
  //   icon: '🔒',
  //   pdfIcon: '🔒',
  //   color: '#10b981',
  //   bg: '#f0fdf4',
  //   border: '#bbf7d0',
  //   refField: 'section',       // field name in JSON items for the reference number
  //   refLabel: 'Section',       // display label for that field
  //   dataKey: 'new_checklist',  // key in ComplianceChecklists.json
  //   description: 'Description shown in the framework selection card',
  // },
};

// ── All framework keys — used for default selection ──
// All 3 frameworks are always pre-selected by default so users never miss
// a legally relevant framework. Users can deselect any they don't need.
const ALL_FRAMEWORK_KEYS = Object.keys(FRAMEWORKS); // ['iso', 'eu', 'nist'] — auto-includes any future frameworks added above

// ── Risk level → context reason shown as advisory banner ──
// Note: fws is now ignored for pre-selection (all are always pre-selected)
// It is kept here for future use (e.g. showing a "mandatory" badge per risk level)
const riskSuggestions = {
  unacceptable: { fws: ['iso', 'eu', 'nist'], reason: 'All three frameworks are critical — your AI may involve prohibited practices requiring immediate legal review.' },
  high:         { fws: ['iso', 'eu', 'nist'], reason: 'All three frameworks apply — full EU AI Act compliance required by August 2027 for High Risk AI.' },
  limited:      { fws: ['iso', 'eu', 'nist'], reason: 'EU AI Act transparency obligations apply. ISO 42001 and NIST RMF are strongly recommended.' },
  minimal:      { fws: ['iso', 'eu', 'nist'], reason: 'EU AI Act may still apply if you operate in or sell to Europe. All three frameworks are shown.' },
};

const euRiskToKey = {
  'Minimal Risk': 'minimal', 'Limited Risk': 'limited',
  'High Risk': 'high', 'Unacceptable / Unsure': 'unacceptable',
};

// ── Build flat items from selected frameworks ──
function buildItems(selectedFrameworks) {
  const items = [];
  selectedFrameworks.forEach(fwKey => {
    const fw = FRAMEWORKS[fwKey];
    const data = checklistData[fw.dataKey];
    if (!data) return;
    data.categories.forEach(cat => {
      cat.items.forEach(item => {
        items.push({
          ...item,
          fwKey, fwLabel: fw.label, fwColor: fw.color, fwBg: fw.bg,
          fwBorder: fw.border, fwIcon: fw.icon,
          refLabel: fw.refLabel, ref: item[fw.refField] || '',
          categoryLabel: cat.category,
        });
      });
    });
  });
  const order = { critical: 0, high: 1, medium: 2 };
  return items.sort((a, b) => (order[a.priority] ?? 9) - (order[b.priority] ?? 9));
}

// ══════════════════════════════════════════════
// DATA SOURCE STATUS BANNER
// ══════════════════════════════════════════════
function DataSourceBanner({ onboarding, riskResult, euClassifierResult, onGoToRiskCalc }) {
  const sources = [];
  if (onboarding) sources.push({ icon: '✅', label: 'Dashboard profile loaded', color: '#10b981' });
  if (riskResult) sources.push({ icon: '✅', label: 'Risk Calculator results loaded', color: '#10b981' });
  if (!riskResult) sources.push({ icon: '⚠️', label: 'Risk Calculator not completed', color: '#f59e0b', action: true });
  if (euClassifierResult) sources.push({ icon: '✅', label: `EU AI Act Classifier — ${euClassifierResult.classification} risk`, color: '#8b5cf6' });

  return (
    <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '12px', padding: '14px 18px', marginBottom: '20px' }}>
      <p style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Data Sources Used for Personalization
      </p>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
        {sources.map((s, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '14px' }}>{s.icon}</span>
            <span style={{ fontSize: '13px', color: s.color, fontWeight: '600' }}>{s.label}</span>
            {s.action && (
              <button onClick={onGoToRiskCalc} style={{ fontSize: '12px', padding: '2px 10px', backgroundColor: '#fef3c7', color: '#92400e', border: '2px solid #fbbf24', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
                Complete now →
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════
// SETUP SCREEN
// ══════════════════════════════════════════════
function SetupScreen({ onComplete, onGoToRiskCalc }) {
  const [step, setStep] = useState(1);
  const [selectedFws, setSelectedFws] = useState([...ALL_FRAMEWORK_KEYS]); // Default: all frameworks pre-selected
  const [role, setRole] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [hasExistingDocs, setHasExistingDocs] = useState('');
  const [riskKey, setRiskKey] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [riskResult, setRiskResult] = useState(null);
  const [euClassifierResult, setEuClassifierResult] = useState(null);
  const [suggestion, setSuggestion] = useState(null);

  useEffect(() => {
    // Layer 1: Dashboard onboarding
    try {
      const saved = localStorage.getItem(userKey('qais_onboarding'));
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.completed) {
          setOnboarding(parsed);
          const rk = euRiskToKey[parsed.euAiRisk] || 'high';
          setRiskKey(rk);
          const sugg = riskSuggestions[rk];
          setSuggestion(sugg);
          setSelectedFws([...ALL_FRAMEWORK_KEYS]); // Always pre-select all 3 frameworks
        }
      }
    } catch {}
    // Layer 2: Risk Calculator results
    try {
      const rc = localStorage.getItem(userKey('qais_risk_result'));
      if (rc) {
        const parsed = JSON.parse(rc);
        setRiskResult(parsed);
        if (!riskKey && parsed.riskLevel) {
          const rk = parsed.riskLevel;
          setRiskKey(rk);
          const sugg = riskSuggestions[rk];
          setSuggestion(sugg);
          setSelectedFws([...ALL_FRAMEWORK_KEYS]); // Always pre-select all 3 frameworks
        }
      }
    } catch {}
    // Layer 2b: EU AI Act Classifier result
    try {
      const eu = localStorage.getItem(userKey('qais_eu_classifier_result'));
      if (eu) {
        const parsed = JSON.parse(eu);
        setEuClassifierResult(parsed);
        // If no risk result yet, use EU classifier classification to set risk key
        if (!riskKey && parsed.classification) {
          const euToRisk = { unacceptable: 'unacceptable', high: 'high', limited: 'limited', minimal: 'minimal' };
          const rk = euToRisk[parsed.classification] || 'high';
          setRiskKey(rk);
          setSuggestion(riskSuggestions[rk]);
          setSelectedFws([...ALL_FRAMEWORK_KEYS]);
        }
      }
    } catch {}
    // Layer 3: Default fallback
    if (!riskKey) {
      setSelectedFws([...ALL_FRAMEWORK_KEYS]); // Always pre-select all frameworks
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleFw = (key) => {
    setSelectedFws(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleComplete = () => {
    const setup = { selectedFws, role, teamSize, hasExistingDocs, riskKey, onboarding, riskResult, euClassifierResult };
    localStorage.setItem(getSetupKey(), JSON.stringify(setup));
    window.dispatchEvent(new CustomEvent('qais-journey-update')); // tells journey bar to re-check
    onComplete(setup);
  };

  const selOption = (opts, value, setter) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {opts.map(opt => (
        <div key={opt.value} onClick={() => setter(opt.value)} style={{
          padding: '12px 16px', borderRadius: '10px', cursor: 'pointer',
          border: `2px solid ${value === opt.value ? '#3b82f6' : '#e2e8f0'}`,
          backgroundColor: value === opt.value ? '#eff6ff' : 'white',
          display: 'flex', alignItems: 'center', gap: '10px'
        }}>
          <div style={{ width: '18px', height: '18px', borderRadius: '50%', border: `2px solid ${value === opt.value ? '#3b82f6' : '#cbd5e1'}`, backgroundColor: value === opt.value ? '#3b82f6' : 'white', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {value === opt.value && <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: 'white' }} />}
          </div>
          <div>
            <p style={{ fontWeight: '600', color: value === opt.value ? '#1e40af' : '#0f172a', margin: 0, fontSize: '14px' }}>{opt.label}</p>
            {opt.hint && <p style={{ fontSize: '12px', color: '#64748b', margin: '2px 0 0' }}>{opt.hint}</p>}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ padding: '40px', maxWidth: '700px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <div style={{ fontSize: '52px', marginBottom: '12px' }}>✏️</div>
        <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: '#0f172a', marginBottom: '8px' }}>
          Compliance Checklist Builder
        </h1>
        <p style={{ fontSize: '22px', color: '#64748b' }}>
          Build your personalized compliance to-do list in 2 quick steps
        </p>
      </div>

      {/* Data source status */}
      <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '12px', padding: '14px 18px', marginBottom: '20px' }}>
        <p style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Personalization Sources
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>{onboarding ? '✅' : '❌'}</span>
            <span style={{ fontSize: '13px', color: onboarding ? '#10b981' : '#64748b', fontWeight: onboarding ? '600' : '400' }}>
              Dashboard profile {onboarding ? `loaded (${onboarding.industry || 'configured'})` : 'not found — complete Dashboard onboarding first'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>{riskResult ? '✅' : '⚠️'}</span>
            <span style={{ fontSize: '13px', color: riskResult ? '#10b981' : '#f59e0b', fontWeight: riskResult ? '600' : '400' }}>
              Risk Calculator {riskResult ? 'results loaded' : 'not completed yet'}
            </span>
            {!riskResult && (
              <button onClick={onGoToRiskCalc} style={{ fontSize: '12px', padding: '3px 10px', backgroundColor: '#fef3c7', color: '#92400e', border: '2px solid #fbbf24', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
                Complete Risk Calculator →
              </button>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>{euClassifierResult ? '✅' : 'ℹ️'}</span>
            <span style={{ fontSize: '13px', color: euClassifierResult ? '#10b981' : '#94a3b8', fontWeight: euClassifierResult ? '600' : '400' }}>
              EU AI Act Classifier {euClassifierResult ? `result loaded (${euClassifierResult.classification} risk)` : 'not completed — optional but recommended'}
            </span>
          </div>
        </div>
      </div>

      {/* Progress steps */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '28px' }}>
        {[1, 2].map(s => (
          <div key={s} style={{ flex: 1, height: '4px', borderRadius: '4px', backgroundColor: s <= step ? '#3b82f6' : '#e2e8f0', transition: 'background-color 0.3s' }} />
        ))}
      </div>

      {/* ── STEP 1: Framework Selection ── */}
      {step === 1 && (
        <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '16px', padding: '28px', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#0f172a', marginBottom: '6px' }}>
            Step 1 — Choose your compliance frameworks
          </h2>
          <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '20px' }}>
            Select one or more frameworks. You can always change this later.
          </p>

          {/* Smart suggestion banner */}
          {suggestion && (
            <div style={{ padding: '12px 14px', backgroundColor: '#eff6ff', border: '2px solid #bfdbfe', borderRadius: '10px', marginBottom: '16px' }}>
              <p style={{ fontSize: '13px', color: '#1e40af', margin: 0 }}>
                <strong>💡 Profile note:</strong> {suggestion.reason}
              </p>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {Object.values(FRAMEWORKS).map(fw => {
              const selected = selectedFws.includes(fw.key);
              // Show badge based on actual legal obligation for this risk level
              const isMandatory = riskKey === 'high' || riskKey === 'unacceptable';



              const itemCount = checklistData[fw.dataKey]?.total_items || 0;
              return (
                <div key={fw.key} onClick={() => toggleFw(fw.key)} style={{
                  padding: '16px 20px', borderRadius: '12px', cursor: 'pointer',
                  border: `2px solid ${selected ? fw.color : '#e2e8f0'}`,
                  backgroundColor: selected ? fw.bg : 'white',
                  transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: '14px'
                }}>
                  <div style={{ width: '22px', height: '22px', borderRadius: '6px', border: `2px solid ${selected ? fw.color : '#cbd5e1'}`, backgroundColor: selected ? fw.color : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {selected && <span style={{ color: 'white', fontSize: '13px', fontWeight: 'bold' }}>✓</span>}
                  </div>
                  <span style={{ fontSize: '22px' }}>{fw.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '2px' }}>
                      <p style={{ fontWeight: '600', color: selected ? fw.color : '#0f172a', margin: 0, fontSize: '15px' }}>{fw.label}</p>
                      {fw.key === 'eu' && (isMandatory
                        ? <span style={{ fontSize: '10px', backgroundColor: '#fef2f2', color: '#991b1b', padding: '2px 8px', borderRadius: '10px', fontWeight: '700' }}>Legally Required</span>
                        : <span style={{ fontSize: '10px', backgroundColor: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: '10px', fontWeight: '700' }}>May Apply</span>
                      )}
                      {fw.key !== 'eu' && <span style={{ fontSize: '10px', backgroundColor: '#dcfce7', color: '#15803d', padding: '2px 8px', borderRadius: '10px', fontWeight: '700' }}>Recommended</span>}
                    </div>
                    <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>{fw.description}</p>
                  </div>
                  <span style={{ fontSize: '12px', color: '#94a3b8', whiteSpace: 'nowrap' }}>{itemCount} items</span>
                </div>
              );
            })}
          </div>

          {selectedFws.length === 0 && (
            <p style={{ fontSize: '13px', color: '#ef4444', marginTop: '12px', textAlign: 'center' }}>
              Please select at least one framework to continue
            </p>
          )}
        </div>
      )}

      {/* ── STEP 2: 3 Quick Questions ── */}
      {step === 2 && (
        <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '16px', padding: '28px', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#0f172a', marginBottom: '6px' }}>
            Step 2 — Three quick questions
          </h2>
          <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '24px' }}>
            These help prioritize your checklist items correctly
          </p>

          <div style={{ marginBottom: '24px' }}>
            <p style={{ fontWeight: '600', color: '#334155', marginBottom: '10px', fontSize: '14px' }}>
              Are you a provider or deployer of AI?
            </p>
            {selOption([
              { value: 'provider', label: 'Provider — we build or develop AI systems', hint: 'Heavier documentation and conformity obligations apply' },
              { value: 'deployer', label: 'Deployer — we use AI built by others', hint: 'Lighter obligations but still accountable for use context' },
              { value: 'both', label: 'Both — we build and use AI systems', hint: 'Full set of provider and deployer obligations apply' },
            ], role, setRole)}
          </div>

          <div style={{ marginBottom: '24px' }}>
            <p style={{ fontWeight: '600', color: '#334155', marginBottom: '10px', fontSize: '14px' }}>
              What is your compliance team size?
            </p>
            {selOption([
              { value: 'none', label: 'No dedicated team — handled by general management' },
              { value: 'small', label: '1–3 people' },
              { value: 'medium', label: '4–10 people' },
              { value: 'large', label: '10+ people' },
            ], teamSize, setTeamSize)}
          </div>

          <div>
            <p style={{ fontWeight: '600', color: '#334155', marginBottom: '10px', fontSize: '14px' }}>
              Do you have existing AI compliance documentation?
            </p>
            {selOption([
              { value: 'none', label: 'None — starting from scratch' },
              { value: 'some', label: 'Some — partial or informal documentation exists' },
              { value: 'substantial', label: 'Substantial — formal documented policies already in place' },
            ], hasExistingDocs, setHasExistingDocs)}
          </div>
        </div>
      )}

      {/* Navigation */}
      <div style={{ display: 'flex', gap: '12px' }}>
        {step > 1 && (
          <button onClick={() => setStep(p => p - 1)} style={{ padding: '14px 24px', backgroundColor: 'white', color: '#334155', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>
            ← Back
          </button>
        )}
        {step === 1 ? (
          <button onClick={() => selectedFws.length > 0 && setStep(2)} disabled={selectedFws.length === 0}
            style={{ flex: 1, padding: '14px', backgroundColor: selectedFws.length === 0 ? '#cbd5e1' : '#3b82f6', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 'bold', cursor: selectedFws.length === 0 ? 'not-allowed' : 'pointer' }}>
            Continue →
          </button>
        ) : (
          <button onClick={() => role && teamSize && hasExistingDocs && handleComplete()}
            disabled={!role || !teamSize || !hasExistingDocs}
            style={{ flex: 1, padding: '14px', backgroundColor: (!role || !teamSize || !hasExistingDocs) ? '#cbd5e1' : '#10b981', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 'bold', cursor: (!role || !teamSize || !hasExistingDocs) ? 'not-allowed' : 'pointer' }}>
            🚀 Build My Checklist
          </button>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════
// MAIN CHECKLIST VIEW
// ══════════════════════════════════════════════
function ChecklistBuilder({ onNavigate }) {
  const [setup, setSetup]                   = useState(null);
  const [checked, setChecked]               = useState({});
  const [evidenceNotes, setEvidenceNotes]    = useState({});
  const [loading, setLoading]               = useState(true);
  const [filterPriority, setFilterPriority] = useState('all');
  const [activeTab, setActiveTab]           = useState('all');
  const [expandedEvidence, setExpandedEvidence] = useState({});
  const [searchText, setSearchText]         = useState('');
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const notesLoadedRef = useRef(false);

  useEffect(() => {
    const init = async () => {
      try {

        const savedSetup = localStorage.getItem(getSetupKey());
        if (savedSetup) {
          const parsed = JSON.parse(savedSetup);
          // Ensure all current frameworks are always included — fixes missing EU AI Act
          const allKeys = Object.keys(FRAMEWORKS);
          if (!parsed.selectedFws || !allKeys.every(k => parsed.selectedFws.includes(k))) {
            parsed.selectedFws = allKeys;
          }
          setSetup(parsed);
        }


      } catch {}
      const progress = await loadProgress();
      setChecked(progress);
      const notes = await loadEvidenceNotes();
      setEvidenceNotes(notes);
      notesLoadedRef.current = true;
      setLoading(false);
    };
    init();

    // Re-check risk result when user returns to this screen
    const syncRiskResult = () => {
      try {
        const rc = localStorage.getItem(userKey('qais_risk_result'));
        if (rc) {
          const parsed = JSON.parse(rc);
          setSetup(prev => {
            if (!prev) return prev;
            if (prev.riskResult) return prev; // already loaded
            const updated = { ...prev, riskResult: parsed };
            localStorage.setItem(getSetupKey(), JSON.stringify(updated));
            return updated;
          });
        }
      } catch {}
    };

    // Fire on tab visibility change (user switches back to this tab)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') syncRiskResult();
    };

    // Fire on window focus (user returns from another window/screen)
    const handleFocus = () => syncRiskResult();

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Watch for setup without riskResult — fires when user navigates back from Risk Calculator
  useEffect(() => {
    if (setup && !setup.riskResult) {
      try {
        const rc = localStorage.getItem(userKey('qais_risk_result'));
        if (rc) {
          const parsed = JSON.parse(rc);
          const updated = { ...setup, riskResult: parsed };
          localStorage.setItem(getSetupKey(), JSON.stringify(updated));
          setSetup(updated);
        }
      } catch {}
    }
  }, [setup]);

  // Task #13 follow-up: debounced autosave for evidence notes — saves ~800ms
  // after the user stops typing, instead of on every keystroke. Guarded by
  // notesLoadedRef so we never overwrite saved notes with the empty {}
  // default before the initial load finishes.
  useEffect(() => {
    if (!notesLoadedRef.current) return;
    const timeout = setTimeout(() => { saveEvidenceNotes(evidenceNotes); }, 800);
    return () => clearTimeout(timeout);
  }, [evidenceNotes]);

  const handleSetupComplete = (s) => {
    // On setup complete, check if Risk Calculator was done in the meantime
    try {
      const rc = localStorage.getItem(userKey('qais_risk_result'));
      if (rc && !s.riskResult) {
        const parsed = JSON.parse(rc);
        s = { ...s, riskResult: parsed };
        localStorage.setItem(getSetupKey(), JSON.stringify(s));
      }
    } catch {}
    setSetup(s);
    setActiveTab('all');
  };
  const handleReset = () => { localStorage.removeItem(getSetupKey()); window.dispatchEvent(new CustomEvent('qais-journey-update')); setSetup(null); setChecked({}); setActiveTab('all'); setFilterPriority('all'); setSearchText(''); };
  const toggleItem = async (id) => { const next = { ...checked, [id]: !checked[id] }; setChecked(next); await saveProgress(next); };
  const toggleEvidence = (id) => setExpandedEvidence(prev => ({ ...prev, [id]: !prev[id] }));
  const updateEvidenceNote = (itemId, evidenceIndex, value) => {
    setEvidenceNotes(prev => {
      const itemNotes = Array.isArray(prev[itemId]) ? [...prev[itemId]] : [];
      itemNotes[evidenceIndex] = value;
      return { ...prev, [itemId]: itemNotes };
    });
  };

  // ── Hooks must be before any early returns ──
  const allItems = useMemo(
    () => setup ? buildItems(setup.selectedFws) : [],
    [setup]
  );

  const visibleItems = useMemo(() => {
    if (!setup) return [];
    let items = allItems;
    if (activeTab !== 'all') items = items.filter(i => i.fwKey === activeTab);
    if (filterPriority !== 'all') items = items.filter(i => i.priority === filterPriority);
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      items = items.filter(i =>
        i.requirement.toLowerCase().includes(q) ||
        i.categoryLabel.toLowerCase().includes(q) ||
        i.ref.toLowerCase().includes(q)
      );
    }
    return items;
  }, [allItems, activeTab, filterPriority, searchText, setup]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', flexDirection: 'column', gap: '16px' }}>
      <div style={{ fontSize: '36px' }}>⏳</div>
      <p style={{ color: '#64748b' }}>Loading your checklist...</p>
    </div>
  );

  if (!setup) return (
    <SetupScreen
      onComplete={handleSetupComplete}
      onGoToRiskCalc={() => onNavigate && onNavigate('risk')}
    />
  );

  // Stats
  const totalItems = allItems.length;
  const doneItems  = allItems.filter(i => checked[i.id]).length;
  const overallPct = totalItems > 0 ? Math.round((doneItems / totalItems) * 100) : 0;
  const getColor   = (p) => p >= 70 ? '#10b981' : p >= 40 ? '#f59e0b' : '#ef4444';

  const fwStats = setup.selectedFws.map(fwKey => {
    const fw = FRAMEWORKS[fwKey];
    const fwItems = allItems.filter(i => i.fwKey === fwKey);
    const fwDone  = fwItems.filter(i => checked[i.id]).length;
    const fwPct   = fwItems.length > 0 ? Math.round((fwDone / fwItems.length) * 100) : 0;
    return { fwKey, fw, total: fwItems.length, done: fwDone, pct: fwPct };
  });

  const priorityCounts = ['critical', 'high', 'medium'].map(p => ({
    p, ...PRIORITY[p],
    total: allItems.filter(i => i.priority === p).length,
    done:  allItems.filter(i => i.priority === p && checked[i.id]).length,
  }));

// ── Helpers for styled PDF (same as Risk Score / EU Classifier) ──
  const loadImageAsBase64 = async (path) => {
    try {
      const response = await fetch(path);
      const blob = await response.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      console.warn('Logo load failed:', e);
      return null;
    }
  };

  const getProgressColor = (pct) => {
    if (pct >= 100) return { bg: '#bbf7d0', border: '#15803d', text: '#14532d', label: 'COMPLETE ✓' };
    if (pct >= 71)  return { bg: '#dcfce7', border: '#16a34a', text: '#166534', label: 'ALMOST COMPLETE' };
    if (pct >= 31)  return { bg: '#fef9c3', border: '#ca8a04', text: '#854d0e', label: 'IN PROGRESS' };
    return            { bg: '#fee2e2', border: '#dc2626', text: '#991b1b', label: 'JUST STARTING' };
  };

  const downloadReport = async () => {
    setPdfGenerating(true);
    await new Promise(resolve => setTimeout(resolve, 50));

    const logoBase64 = await loadImageAsBase64('/logo.png');
    const progressColor = getProgressColor(overallPct);

    // Per-framework progress cards
    const fwCardsHTML = fwStats.map(s => `
      <div style="background: #f8fafc; border-left: 4px solid #0e7c7c; padding: 12px 16px; margin-bottom: 10px; border-radius: 6px; page-break-inside: avoid;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
          <span style="font-weight: 700; color: #0e7c7c; font-size: 15px;">${s.fw.pdfIcon || ''} ${s.fw.label}</span>
          <span style="font-weight: 700; color: #0e7c7c; font-size: 14px;">${s.done}/${s.total} (${s.pct}%)</span>
        </div>
        <div style="background: #e2e8f0; height: 8px; border-radius: 4px; overflow: hidden;">
          <div style="background: #0e7c7c; height: 100%; width: ${s.pct}%;"></div>
        </div>
      </div>
    `).join('');

    // Items per priority bucket
    const priorityColors = {
      critical: { bg: '#fee2e2', border: '#dc2626', text: '#991b1b', label: 'CRITICAL PRIORITY' },
      high:     { bg: '#ffedd5', border: '#ea580c', text: '#9a3412', label: 'HIGH PRIORITY' },
      medium:   { bg: '#fef9c3', border: '#ca8a04', text: '#854d0e', label: 'MEDIUM PRIORITY' },
    };

    const buildItemCardsHTML = (priority) => {
      const items = allItems.filter(i => i.priority === priority);
      if (!items.length) return '';
      const pc = priorityColors[priority];
      const itemsHTML = items.map((item, i) => `
        <div style="background: white; border: 1px solid #e2e8f0; border-left: 4px solid ${pc.border}; padding: 12px 16px; margin-bottom: 8px; border-radius: 6px; page-break-inside: avoid;">
          <div style="display: flex; align-items: flex-start; gap: 10px;">
            <span style="font-size: 16px; color: ${checked[item.id] ? '#15803d' : '#94a3b8'}; flex-shrink: 0;">${checked[item.id] ? '✓' : '○'}</span>
            <div style="flex: 1;">
              <div style="font-weight: 600; color: #0f172a; font-size: 14px; margin-bottom: 4px;">
                <span style="background: ${pc.bg}; color: ${pc.text}; padding: 1px 8px; border-radius: 4px; font-size: 11px; font-weight: 700; margin-right: 6px;">${item.fwLabel}</span>
                ${item.requirement}
              </div>
              <div style="font-size: 12px; color: #64748b; margin-bottom: 4px;">
                <strong>${item.refLabel}:</strong> ${item.ref} &nbsp;|&nbsp; <strong>Category:</strong> ${item.categoryLabel}
              </div>
              ${item.evidence_needed?.length ? `<div style="font-size: 12px; color: #475569;"><strong>Evidence needed:</strong> ${item.evidence_needed.join(' · ')}</div>` : ''}
            </div>
          </div>
        </div>
      `).join('');

      return `
        <h2 style="color: ${pc.border}; border-bottom: 2px solid ${pc.border}; font-size: 22px; margin-top: 24px;">${pc.label} <span style="font-size: 14px; color: #64748b; font-weight: normal;">(${items.length} items)</span></h2>
        ${itemsHTML}
      `;
    };

    const htmlContent = `
      <div style="font-family: 'Inter', 'Helvetica', sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; background: white; color: #1e293b; line-height: 1.6; font-size: 15px;">
        ${logoBase64 ? `<div style="text-align: center; margin-bottom: 8px;"><img src="${logoBase64}" style="max-width: 180px; max-height: 90px;" alt="QuantumAI Shield" /></div>` : ''}
        <h1 style="color: #0e7c7c; border-bottom: 3px solid #0e7c7c; padding-bottom: 10px; font-size: 28px; text-align: center;">Compliance Checklist Report</h1>
        <p style="text-align: center;"><strong>Generated by QuantumAI Shield</strong><br>${new Date().toLocaleDateString()}</p>

        <!-- Overall progress band -->
        <div style="background: ${progressColor.bg}; border: 3px solid ${progressColor.border}; border-radius: 12px; padding: 20px; margin: 20px 0; text-align: center;">
          <div style="font-size: 14px; color: ${progressColor.text}; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase; margin-bottom: 8px;">Overall Progress</div>
          <div style="font-size: 32px; color: ${progressColor.text}; font-weight: 800; margin-bottom: 6px;">${doneItems} / ${totalItems} (${overallPct}%)</div>
          <div style="font-size: 16px; color: ${progressColor.text}; font-weight: 700;">${progressColor.label}</div>
        </div>

        <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 22px;">Setup</h2>
        <p><strong>Frameworks:</strong> ${setup.selectedFws.map(k => FRAMEWORKS[k].label).join(', ')}<br>
        <strong>Role:</strong> ${setup.role || 'Not specified'}<br>
        <strong>Team size:</strong> ${setup.teamSize || 'Not specified'}
        ${setup.onboarding?.industry ? `<br><strong>Industry:</strong> ${setup.onboarding.industry}` : ''}</p>

        <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 22px;">Progress by Framework</h2>
        ${fwCardsHTML}

        ${buildItemCardsHTML('critical')}
        ${buildItemCardsHTML('high')}
        ${buildItemCardsHTML('medium')}

        <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 22px;">Disclaimer</h2>
        <p style="font-size: 12px; color: #64748b;">AI‑generated compliance guidance – not legal advice. Always verify with a qualified professional. For questions, contact QuantumAIshield support. Blockchain records are permanent – please confirm before sealing.</p>
      </div>
    `;

    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.style.width = '800px';
    container.style.backgroundColor = 'white';
    container.style.zIndex = '-1';
    container.innerHTML = htmlContent;
    document.body.appendChild(container);

    try {
      await new Promise(resolve => setTimeout(resolve, 300));
      const canvas = await html2canvas(container, { scale: 2, backgroundColor: '#ffffff', logging: false, useCORS: false });

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth  = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const HEADER_HEIGHT = 28;
      const FOOTER_HEIGHT = 22;
      const SIDE_MARGIN   = 14; // mm — page-level left/right margin (was 0, edge-to-edge)

      const usableWidth  = pdfWidth - 2 * SIDE_MARGIN;
      const usableHeight = pdfHeight - HEADER_HEIGHT - FOOTER_HEIGHT;
      const imgWidth     = usableWidth;
      const imgHeight    = (canvas.height * imgWidth) / canvas.width;

      // ── Smart slicing: detect blank rows (uniform-color rows safe to slice at) ──
      const canvasCtx = canvas.getContext('2d');
      const imageData = canvasCtx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      const cw = canvas.width;

      const isRowBlank = (y) => {
        // A row is "safe to slice at" if it has very few dark pixels
        let darkPixels = 0;
        for (let x = 0; x < cw; x += 4) {
          const i = (y * cw + x) * 4;
          if (data[i] < 100 || data[i + 1] < 100 || data[i + 2] < 100) {
            darkPixels++;
            if (darkPixels > 2) return false; // 3+ dark pixels = has text
          }
        }
        return true;
      };

      const pxPerMm = canvas.height / imgHeight;
      const usableHeightPx = Math.floor(usableHeight * pxPerMm);

      const blankRows = [];
      for (let y = 0; y < canvas.height; y += 2) {
        if (isRowBlank(y)) blankRows.push(y);
      }


const pageBreaks = [0];
      let currentY = 0;
      while (currentY < canvas.height) {
        const target = currentY + usableHeightPx;
        if (target >= canvas.height) break;
        let breakAt = target;
        const minAllowed = currentY + usableHeightPx * 0.35;
        for (let i = blankRows.length - 1; i >= 0; i--) {
          if (blankRows[i] <= target && blankRows[i] > minAllowed) {
            breakAt = blankRows[i];
            break;
          }
        }
        pageBreaks.push(breakAt);
        currentY = breakAt;
      }

      // ── Drop the last page if it has no meaningful content (avoid blank trailing page) ──
      // Find the last non-blank row in the canvas
      let lastContentRow = 0;
      for (let y = canvas.height - 1; y >= 0; y -= 2) {
        if (!isRowBlank(y)) { lastContentRow = y; break; }
      }
      // If the last page break is AFTER the last content row, that page would be blank — remove it
      while (pageBreaks.length > 1 && pageBreaks[pageBreaks.length - 1] >= lastContentRow - 20) {
        pageBreaks.pop();
      }
      const totalPages = pageBreaks.length;

      const drawHeaderFooter = (pageNum) => {
        if (pageNum > 1) {
          const titleText = 'Quantum AI Shield · Compliance Checklist';
          pdf.setFontSize(11);
          pdf.setTextColor(80, 80, 80);
          const logoW  = logoBase64 ? 10 : 0;
          const gap    = logoBase64 ? 3  : 0;
          const titleW = pdf.getTextWidth(titleText);
          const groupW = logoW + gap + titleW;
          const groupStartX = (pdfWidth - groupW) / 2;
          if (logoBase64) { try { pdf.addImage(logoBase64, 'PNG', groupStartX, 6, 10, 10); } catch {} }
          pdf.text(titleText, groupStartX + logoW + gap, 13);
          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.2);
          pdf.line(SIDE_MARGIN + 4, HEADER_HEIGHT - 4, pdfWidth - SIDE_MARGIN - 4, HEADER_HEIGHT - 4);
        }
        pdf.setFontSize(9);
        pdf.setTextColor(120, 120, 120);
        pdf.line(SIDE_MARGIN + 4, pdfHeight - FOOTER_HEIGHT + 6, pdfWidth - SIDE_MARGIN - 4, pdfHeight - FOOTER_HEIGHT + 6);
        pdf.text('www.QuantumAIShield.com',          SIDE_MARGIN + 4,            pdfHeight - 8);
        pdf.text(`Page ${pageNum} of ${totalPages}`, pdfWidth / 2,               pdfHeight - 8, { align: 'center' });
        pdf.text(new Date().toLocaleDateString(),    pdfWidth - SIDE_MARGIN - 4, pdfHeight - 8, { align: 'right' });
      };

      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        if (pageNum > 1) pdf.addPage();
        const startPx = pageBreaks[pageNum - 1];
        const endPx   = pageNum < totalPages ? pageBreaks[pageNum] : canvas.height;
        const slicePx = endPx - startPx;
        const sliceMm = slicePx / pxPerMm;

        const tempCanvas = document.createElement('canvas');
        tempCanvas.width  = canvas.width;
        tempCanvas.height = slicePx;
        const tempCtx = tempCanvas.getContext('2d');
        tempCtx.fillStyle = '#ffffff';
        tempCtx.fillRect(0, 0, canvas.width, slicePx);
        tempCtx.drawImage(canvas, 0, startPx, canvas.width, slicePx, 0, 0, canvas.width, slicePx);
        const sliceData = tempCanvas.toDataURL('image/png');

        pdf.addImage(sliceData, 'PNG', SIDE_MARGIN, HEADER_HEIGHT, imgWidth, sliceMm);
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pdfWidth, HEADER_HEIGHT, 'F');
        pdf.rect(0, pdfHeight - FOOTER_HEIGHT, pdfWidth, FOOTER_HEIGHT, 'F');
        drawHeaderFooter(pageNum);
      }

      pdf.save(`QuantumAIShield_Checklist_${Date.now()}.pdf`);
    } catch (err) {
      console.error('PDF generation error:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      document.body.removeChild(container);
      setPdfGenerating(false);
    }
  };




  return (
    <div style={{ padding: '32px 24px', maxWidth: '1100px', margin: '0 auto', backgroundColor: '#f8fafc', minHeight: '100vh' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#0f172a', margin: '0 0 4px' }}>
            ✏️ Compliance Checklist
          </h1>
          <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
            {setup.selectedFws.map(k => FRAMEWORKS[k].label).join(' · ')}
            {setup.onboarding?.industry ? ` · ${setup.onboarding.industry}` : ''}
            {setup.role ? ` · ${setup.role.charAt(0).toUpperCase() + setup.role.slice(1)}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>

        <button
            onClick={downloadReport}
            disabled={pdfGenerating}
            style={{
              fontSize: '13px', padding: '8px 14px',
              backgroundColor: pdfGenerating ? '#cbd5e1' : '#3b82f6',
              color: 'white', border: 'none', borderRadius: '8px',
              cursor: pdfGenerating ? 'wait' : 'pointer',
              fontWeight: '600'
            }}
          >
            {pdfGenerating ? '⏳ Generating PDF...' : '⬇️ Download as PDF'}
        </button>

          <button onClick={handleReset} style={{ fontSize: '13px', padding: '8px 14px', backgroundColor: 'white', color: '#64748b', border: '2px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer' }}>
            ⚙️ Rebuild
          </button>
        </div>
      </div>

      {/* ── Data source banner ── */}
      <DataSourceBanner
        onboarding={setup.onboarding}
        riskResult={setup.riskResult}
        onGoToRiskCalc={() => onNavigate && onNavigate('risk')}
      />

      {/* ── Overall progress ── */}
      <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '16px', padding: '24px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 style={{ fontSize: '17px', fontWeight: 'bold', color: '#0f172a', margin: '0 0 3px' }}>Overall Compliance Progress</h2>
            <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>{doneItems} of {totalItems} requirements completed</p>
          </div>
          <div style={{ fontSize: '38px', fontWeight: 'bold', color: getColor(overallPct) }}>{overallPct}%</div>
        </div>
        <div style={{ height: '12px', backgroundColor: '#f1f5f9', borderRadius: '6px', overflow: 'hidden', marginBottom: '20px' }}>
          <div style={{ width: `${overallPct}%`, height: '100%', backgroundColor: getColor(overallPct), borderRadius: '6px', transition: 'width 0.8s ease' }} />
        </div>

        {/* Per-framework bars */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '16px' }}>
          {fwStats.map(({ fwKey, fw, total, done, pct }) => (
            <div key={fwKey} style={{ backgroundColor: fw.bg, borderRadius: '10px', padding: '14px', border: `2px solid ${fw.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', fontWeight: '600', color: fw.color }}>{fw.icon} {fw.shortLabel}</span>
                <span style={{ fontSize: '13px', fontWeight: 'bold', color: fw.color }}>{pct}%</span>
              </div>
              <div style={{ height: '6px', backgroundColor: `${fw.color}25`, borderRadius: '3px', overflow: 'hidden', marginBottom: '6px' }}>
                <div style={{ width: `${pct}%`, height: '100%', backgroundColor: fw.color, borderRadius: '3px', transition: 'width 0.8s' }} />
              </div>
              <p style={{ fontSize: '11px', color: '#64748b', margin: 0 }}>{done}/{total} items</p>
            </div>
          ))}
        </div>

        {/* Priority summary chips */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {priorityCounts.filter(p => p.total > 0).map(({ p, label, color, bg, total, done }) => (
            <div key={p} style={{ padding: '5px 12px', backgroundColor: bg, borderRadius: '20px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: color }} />
              <span style={{ fontSize: '12px', fontWeight: '600', color }}>{label}: {done}/{total}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Framework tabs */}
        <div style={{ display: 'flex', gap: '3px', backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '3px' }}>
          {['all', ...setup.selectedFws].map(tab => {
            const fw = tab === 'all' ? null : FRAMEWORKS[tab];
            return (
              <button key={tab} onClick={() => setActiveTab(tab)} style={{ padding: '6px 12px', borderRadius: '7px', border: 'none', backgroundColor: activeTab === tab ? '#3b82f6' : 'transparent', color: activeTab === tab ? 'white' : '#64748b', fontSize: '13px', fontWeight: activeTab === tab ? '600' : '400', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s' }}>
                {tab === 'all' ? 'All Frameworks' : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    {React.isValidElement(fw.icon) && fw.icon.type === EUFlagIcon
                      ? React.cloneElement(fw.icon, { size: 32 })
                      : fw.icon}
                    {fw.shortLabel}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Priority filter */}
        <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} style={{ padding: '8px 12px', fontSize: '13px', border: '2px solid #e2e8f0', borderRadius: '8px', backgroundColor: 'white', cursor: 'pointer', outline: 'none' }}>
          <option value="all">All Priorities</option>
          <option value="critical">🔴 Critical only</option>
          <option value="high">🟡 High only</option>
          <option value="medium">🔵 Medium only</option>
        </select>

        {/* Search */}
        <input type="text" value={searchText} onChange={e => setSearchText(e.target.value)}
          placeholder="🔍 Search requirements..."
          style={{ flex: 1, minWidth: '200px', padding: '8px 12px', fontSize: '13px', border: '2px solid #e2e8f0', borderRadius: '8px', outline: 'none' }} />

        <span style={{ fontSize: '13px', color: '#94a3b8', whiteSpace: 'nowrap' }}>
          {visibleItems.length} of {totalItems}
        </span>
      </div>

      {/* ── Checklist items ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {visibleItems.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', backgroundColor: 'white', borderRadius: '12px', border: '2px solid #e2e8f0', color: '#64748b' }}>
            <p style={{ fontSize: '16px', margin: '0 0 8px' }}>No items match your filters</p>
            <p style={{ fontSize: '13px', margin: 0 }}>Try clearing the search box or changing the priority filter</p>
          </div>
        )}

        {['critical', 'high', 'medium'].map(priority => {
          const groupItems = visibleItems.filter(i => i.priority === priority);
          if (!groupItems.length) return null;
          const pr = PRIORITY[priority];
          const groupDone = groupItems.filter(i => checked[i.id]).length;

          return (
            <React.Fragment key={priority}>
              {/* Group header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 4px', marginTop: priority === 'critical' ? 0 : '16px' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: pr.color, flexShrink: 0 }} />
                <span style={{ fontSize: '12px', fontWeight: '700', color: pr.color, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                  {pr.label} Priority
                </span>
                <span style={{ fontSize: '11px', color: '#94a3b8', backgroundColor: '#f1f5f9', padding: '2px 8px', borderRadius: '10px' }}>
                  {groupDone}/{groupItems.length} completed
                </span>
                <div style={{ flex: 1, height: '1px', backgroundColor: '#e2e8f0' }} />
              </div>

              {/* Items */}
              {groupItems.map(item => {
                const isChecked  = !!checked[item.id];
                const isExpanded = !!expandedEvidence[item.id];
                const fw = FRAMEWORKS[item.fwKey];
                return (
                  <div key={item.id} style={{
                    backgroundColor: isChecked ? '#f0fdf4' : 'white',
                    border: `2px solid ${isChecked ? '#bbf7d0' : pr.border}`,
                    borderRadius: '12px', padding: '14px 16px', transition: 'all 0.15s',
                  }}>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      {/* Checkbox */}
                      <div onClick={() => toggleItem(item.id)} style={{ width: '22px', height: '22px', borderRadius: '6px', border: `2px solid ${isChecked ? '#10b981' : '#cbd5e1'}`, backgroundColor: isChecked ? '#10b981' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px', cursor: 'pointer', transition: 'all 0.15s' }}>
                        {isChecked && <span style={{ color: 'white', fontSize: '13px', fontWeight: 'bold' }}>✓</span>}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Badges */}
                        <div style={{ display: 'flex', gap: '6px', marginBottom: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                          <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', fontWeight: '600', backgroundColor: fw.bg, color: fw.color, border: `1px solid ${fw.border}` }}>
                            {fw.icon} {fw.shortLabel}
                          </span>
                          <span style={{ fontSize: '11px', color: '#94a3b8' }}>{item.categoryLabel}</span>
                          <span style={{ fontSize: '11px', color: '#94a3b8' }}>· {item.refLabel} {item.ref}</span>
                        </div>

                        {/* Requirement text */}
                        <p style={{ fontSize: '14px', fontWeight: '600', color: isChecked ? '#94a3b8' : '#0f172a', margin: '0 0 8px', textDecoration: isChecked ? 'line-through' : 'none', lineHeight: '1.5' }}>
                          {item.requirement}
                        </p>

                        {/* Evidence toggle */}
                        {item.evidence_needed?.length > 0 && (
                          <>
                            <button onClick={() => toggleEvidence(item.id)} style={{ fontSize: '12px', color: '#3b82f6', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ fontSize: '10px' }}>{isExpanded ? '▼' : '▶'}</span>
                              {isExpanded ? 'Hide' : 'Show'} evidence needed ({item.evidence_needed.length})
                            </button>
                            {isExpanded && (
                              <div style={{ marginTop: '10px', padding: '10px 14px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                <p style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                  Documentation / Evidence Required:
                                </p>
                                <p style={{ fontSize: '11px', color: '#94a3b8', margin: '0 0 10px', fontStyle: 'italic' }}>
                                  Notes below are for your own recordkeeping — not reviewed or verified by QuantumAI Shield.
                                </p>
                                {item.evidence_needed.map((ev, i) => (
                                  <div key={i} style={{ marginBottom: '10px' }}>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '5px' }}>
                                      <span style={{ color: '#3b82f6', fontSize: '12px', flexShrink: 0 }}>→</span>
                                      <span style={{ fontSize: '13px', color: '#334155' }}>{ev}</span>
                                    </div>
                                    <input
                                      type="text"
                                      value={evidenceNotes[item.id]?.[i] || ''}
                                      onChange={(e) => updateEvidenceNote(item.id, i, e.target.value)}
                                      placeholder='Evidence location / notes (e.g. "see SharePoint/Policies/DataGovernance.pdf" or "signed by CTO 2026-03-01")'
                                      style={{
                                        width: '100%', boxSizing: 'border-box', marginLeft: '20px',
                                        padding: '6px 10px', fontSize: '12px', color: '#0f172a',
                                        border: '1px solid #cbd5e1', borderRadius: '6px', outline: 'none',
                                      }}
                                    />
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>

      {/* Footer disclaimer */}
      <div style={{ marginTop: '32px', padding: '14px 16px', backgroundColor: '#fef3c7', border: '2px solid #fbbf24', borderRadius: '10px' }}>
        <p style={{ margin: 0, color: '#92400e', fontSize: '13px' }}>
          <strong>⚠️ Disclaimer:</strong>  AI‑generated compliance guidance – not legal advice. Always verify with a qualified professional. For questions, contact QuantumAIshield support. Blockchain records are permanent – please confirm before sealing.
        </p>
      </div>
    </div>
  );
}

export default ChecklistBuilder;